import { useForm } from "react-hook-form";
import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse } from "@fortawesome/free-solid-svg-icons";
import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons";

function ResetPassword() {
  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null); // null | "success" | "error"
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const token = searchParams.get("token");

  const onSubmit = async (data) => {
    setLoading(true);
    setStatus(null);
    try {
      const response = await fetch("/api/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword: data.newPassword }),
      });
      const result = await response.json();
      if (response.ok) {
        setStatus("success");
        setMessage(result.message || "Password updated successfully.");
        setTimeout(() => navigate("/login"), 3000);
      } else {
        setStatus("error");
        setMessage(result.message || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setMessage("Error connecting to server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
        <div className="col-12 col-sm-8 col-md-6 col-lg-5 p-4 border rounded shadow-sm bg-white text-center">
          <FontAwesomeIcon icon={faHouse} size="2x" className="text-danger mb-2" />
          <h5>Invalid Reset Link</h5>
          <p className="text-muted small">This link is missing a token. Please request a new password reset.</p>
          <Link to="/forgot-password" className="btn btn-primary btn-sm">Request Reset</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: "70vh" }}>
      <div className="col-12 col-sm-8 col-md-6 col-lg-5 p-4 border rounded shadow-sm bg-white">
        <div className="text-center mb-3">
          <FontAwesomeIcon icon={faHouse} size="2x" className="text-primary mb-2" />
          <h4 className="mb-0">Set a new password</h4>
          <p className="text-muted small mt-1">Choose a strong password for your account.</p>
        </div>

        {status === "success" ? (
          <div className="alert alert-success" role="alert">
            {message}
            <div className="text-muted small mt-1">Redirecting to login…</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {status === "error" && (
              <div className="alert alert-danger py-2 small">{message}</div>
            )}

            <div className="form-group mb-3">
              <label htmlFor="new-password">New Password <span className="text-danger">*</span></label>
              <div className="position-relative mt-1">
                <input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="At least 8 characters"
                  {...register("newPassword", {
                    required: "Password is required.",
                    minLength: { value: 8, message: "Password must be at least 8 characters." },
                    pattern: {
                      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/,
                      message: "Must contain uppercase, lowercase, and a number.",
                    },
                  })}
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ textDecoration: "none", zIndex: 10 }}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-secondary" />
                </button>
              </div>
              {errors.newPassword && (
                <span className="text-danger small">{errors.newPassword.message}</span>
              )}
            </div>

            <div className="form-group mb-3">
              <label htmlFor="confirm-password">Confirm Password <span className="text-danger">*</span></label>
              <div className="position-relative mt-1">
                <input
                  id="confirm-password"
                  type={showConfirm ? "text" : "password"}
                  className="form-control"
                  placeholder="Repeat your password"
                  {...register("confirmPassword", {
                    required: "Please confirm your password.",
                    validate: (val) =>
                      val === watch("newPassword") || "Passwords do not match.",
                  })}
                />
                <button
                  type="button"
                  className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                  onClick={() => setShowConfirm(!showConfirm)}
                  style={{ textDecoration: "none", zIndex: 10 }}
                  tabIndex={-1}
                >
                  <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} className="text-secondary" />
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="text-danger small">{errors.confirmPassword.message}</span>
              )}
            </div>

            <div className="d-grid mb-3">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Updating…" : "Update Password"}
              </button>
            </div>

            <div className="text-center small">
              <Link to="/login" className="text-decoration-none">Back to Login</Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
